import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DollarSign, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { EditableLineItemRow } from "./EditableLineItemRow";

interface LineItem {
  id?: string;
  product_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  line_total: number;
}

interface EstimateSummaryPanelProps {
  lineItems: LineItem[];
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRate: number;
  onRemoveItem?: (itemId: string) => void;
  onUpdateItem?: (itemId: string, updates: Partial<LineItem>) => void;
  className?: string;
}

export function EstimateSummaryPanel({
  lineItems,
  subtotal,
  taxAmount,
  total,
  taxRate,
  onRemoveItem,
  onUpdateItem,
  className,
}: EstimateSummaryPanelProps) {
  const itemCount = lineItems.length;

  return (
    <Card className={cn("flex flex-col h-full w-full", className)}>
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Estimate Summary
          </CardTitle>
          <Badge variant="secondary" className="text-sm">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </Badge>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="flex flex-col p-0 h-[calc(100%-80px)] w-full">
        {/* Line Items */}
        <div className="flex-1 px-4 md:px-6 py-4 overflow-y-auto overflow-x-hidden w-full min-h-0">
          {lineItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <DollarSign className="w-12 h-12 mb-2 opacity-20" />
              <p className="text-sm">No items added yet</p>
              <p className="text-xs">Select products to add them</p>
            </div>
          ) : (
            <div className="space-y-3 w-full max-w-full">
              {lineItems.map((item, index) => (
                <EditableLineItemRow
                  key={item.id || index}
                  item={item}
                  onUpdate={(id, updates) => onUpdateItem?.(id, updates)}
                  onDelete={(id) => onRemoveItem?.(id)}
                />
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Totals */}
        <div className="px-4 md:px-6 py-4 space-y-3 bg-muted/30 shrink-0">
          <div className="flex justify-between text-sm md:text-base">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm md:text-base">
            <span className="text-muted-foreground">Tax ({taxRate}%)</span>
            <span className="font-medium">${taxAmount.toFixed(2)}</span>
          </div>
          
          <Separator />
          
          <div className="flex justify-between items-center">
            <span className="text-base md:text-lg font-semibold">Total</span>
            <span className="text-xl md:text-2xl font-bold text-primary">
              ${total.toFixed(2)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
